import { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import moneyFormat from 'helpers/moneyFormat';

const EMPTY_COLUMNS = {};

// Filtros de período: 'YYYY-MM' (puntual) o ['YYYY-MM','YYYY-MM'] (rango, Mantis #254).
const PERIODO_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// Períodos válidos de un valor de filtro, ordenados.
const periodosDe = (valor) =>
    (Array.isArray(valor) ? valor : [valor])
        .filter(v => typeof v === 'string' && PERIODO_RE.test(v.trim()))
        .map(v => v.trim())
        .sort();

// Todos los meses entre dos períodos, inclusive: ('2026-01','2026-03') → 3 meses.
const mesesDelRango = (desde, hasta) => {
    const meses = [];
    let [anio, mes] = desde.split('-').map(Number);
    const [anioFin, mesFin] = hasta.split('-').map(Number);
    while (anio * 100 + mes <= anioFin * 100 + mesFin) {
        meses.push(`${anio}-${String(mes).padStart(2, '0')}`);
        mes++;
        if (mes > 12) { mes = 1; anio++; }
    }
    return meses;
};

// Días calendario de un período (el día 0 del mes siguiente es el último del mes).
const diasDelMes = (periodo) => {
    const [anio, mes] = periodo.split('-').map(Number);
    return new Date(anio, mes, 0).getDate();
};

const formatearPeriodo = (periodo) => {
    const [anio, mes] = periodo.split('-');
    return `${mes.padStart(2, '0')}/${anio}`;
};

const DataLabel = ({
    url,
    filters,
    label,
    isMoney = true,
    countDateColumn = null,
    countDayColumn = null,
    columns = EMPTY_COLUMNS,
    filtersReady = true,
    updateCount = 0,
    explanationText = null
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [result, setResult] = useState();
    const [displayText, setDisplayText] = useState(explanationText);

    useEffect(() => {
        let text = explanationText;

        if (filters && filters.length > 0) {
            const periodoFilter = filters.find(filter =>
                filter.id && filter.id.startsWith('periodo_') && filter.value
            );

            if (periodoFilter && periodoFilter.value) {
                const periodos = periodosDe(periodoFilter.value);
                if (periodos.length > 1) {
                    text = `${formatearPeriodo(periodos[0])} a ${formatearPeriodo(periodos[periodos.length - 1])}`;
                } else if (periodos.length === 1) {
                    text = formatearPeriodo(periodos[0]);
                }
            } else {
                if (!text) text = '12 meses';
            }
        } else {
            if (!text) text = '12 meses';
        }

        setDisplayText(text);
    }, [filters, explanationText]);

    const filtersKey = useMemo(
        () => JSON.stringify(filters ?? []),
        [filters]
    );

    useEffect(() => {
        const fetchData = async () => {
            if (!result) {
                setIsLoading(true);
            }
            if (countDateColumn && countDayColumn) {
                const periodoFilter = filters.find(i => i.id === countDateColumn);
                const diaFilter = filters.find(i => i.id === countDayColumn);

                if (periodoFilter && periodoFilter.value && diaFilter && diaFilter.value) {
                    // Un día puntual dentro de cada mes del período filtrado.
                    const periodos = periodosDe(periodoFilter.value);
                    const meses = periodos.length > 1
                        ? mesesDelRango(periodos[0], periodos[periodos.length - 1])
                        : [null];
                    setResult(meses.length);
                    setIsLoading(false);
                    return;
                }
            }
            if (countDateColumn) {
                if (countDateColumn.includes('periodo')) {
                    const periodo = filters.find(i => i.id == countDateColumn);
                    const periodos = periodo ? periodosDe(periodo.value) : [];
                    if (!periodos.length) {
                        setIsError(true);
                        return;
                    }
                    // Días calendario del rango (o del mes, si es un período puntual).
                    const meses = periodos.length > 1
                        ? mesesDelRango(periodos[0], periodos[periodos.length - 1])
                        : [periodos[0]];
                    setResult(meses.reduce((total, mes) => total + diasDelMes(mes), 0));
                    setIsLoading(false);
                    return;
                }
                const fechas = filters.find(i => i.id == countDateColumn);
                if (!fechas || fechas.value.length != 2) {
                    setIsError(true);
                    return;
                }
                const diff = fechas.value[1].diff(fechas.value[0], 'day', true);
                setResult(Math.floor(diff) - 1);
                setIsLoading(false);
                return;
            }
            try {
                let currentFilters = [...filters];
                currentFilters = currentFilters.filter(filter =>
                    Object.keys(columns).some(columnKey => columnKey === filter.id)
                );
                const response = await fetch(url + '&' + new URLSearchParams({
                    filters: JSON.stringify(currentFilters ?? [])
                }), {
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': localStorage.getItem('token'),
                    }
                });
                const json = await response.json();
                setResult(json.data[0].total || 0);
            } catch (error) {
                setIsError(true);
                console.error(error);
                return;
            }
            setIsError(false);
            setIsLoading(false);
        };
        fetchData();
    }, [filtersKey, updateCount, url, columns, countDateColumn, countDayColumn, filtersReady]);

    if (isError) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '9px' }}>
                <WarningAmberIcon sx={{ color: 'primary.main', width: 32, height: 32 }} />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: '9px' }}>
            <Box sx={{ mr: 'auto' }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.5,
                    }}
                >
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {isLoading ? (
                        <CircularProgress size={20} color="primary" />
                    ) : (
                        <>
                            <Box
                                component="span"
                                sx={{
                                    backgroundColor: 'primary.main',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    px: '6px',
                                    py: '2px',
                                    display: 'inline-block',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.8rem' }}
                                >
                                    {isMoney ? moneyFormat(result) : result}
                                </Typography>
                            </Box>
                            {displayText && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary', mt: '2px', fontStyle: 'italic' }}
                                >
                                    {displayText}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DataLabel;
